import styled from '@emotion/styled';
import * as system from 'styled-system';
import InfiniteScroll from 'react-infinite-scroll-component';
import Image from 'next/image';
import OutlinedInput from '@mui/material/OutlinedInput';

export type ComposeType = system.SpaceProps &
  system.GridProps &
  system.ColorProps &
  system.BackgroundProps &
  system.LayoutProps &
  system.BorderProps &
  system.PositionProps &
  system.FlexboxProps &
  system.ShadowProps &
  system.TypographyProps;

export const Compose = system.compose(
  system.space,
  system.background,
  system.grid,
  system.color,
  system.layout,
  system.border,
  system.position,
  system.shadow,
  system.flexbox,
  system.typography,
);

export const StyledDiv = styled.div<
  ComposeType & {
    isCenter?: boolean;
    gap?: string;
    cursor?: string;
    transition?: string;
    direction?: string;
    lineHeight?: string;
    fontFamily?: string;
  }
>`
  display: ${({ isCenter }): string => (isCenter ? 'flex' : 'block')};
  justify-content: ${({ isCenter }): string => (isCenter ? 'center' : 'unset')};
  align-items: ${({ isCenter }): string => (isCenter ? 'center' : 'unset')};
  cursor: ${({ cursor }): string | undefined => cursor};
  transition: ${({ transition }): string | undefined => transition || ''};
  gap: ${({ gap }): string | undefined => gap}px;
  line-height: ${({ lineHeight }): string | undefined => lineHeight};
  direction: ${({ direction }): string | undefined => direction || 'inherit'};
  font-family: ${({ fontFamily, theme }): string | undefined =>
    fontFamily || theme.fonts.regular};
  box-sizing: border-box;
  ${Compose};
`;

export const StyledSpan = styled.span<ComposeType>`
  cursor: ${({ cursor }): string | undefined => cursor};
  font-family: ${({ fontFamily, theme }): string | undefined =>
    fontFamily || theme.fonts.regular};
  ${Compose};
`;

export const StyledLink = styled.div<ComposeType>`
  color: ${({ theme }): string => theme.colors.berry60};
  display: ${({ isCenter }): string => (isCenter ? 'flex' : 'block')};
  justify-content: ${({ isCenter }): string => (isCenter ? 'center' : 'unset')};
  align-items: ${({ isCenter }): string => (isCenter ? 'center' : 'unset')};
  ${Compose};
`;
export const StyledInfiniteScroll = styled(InfiniteScroll)`
  overflow: unset !important;
`;

export const StyledSignedText = styled(StyledDiv)<{
  value: number;
  hasBg?: boolean;
  px?: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  direction: ltr;
  margin: 0 auto;
  width: fit-content;
  height: ${({ theme }): string => theme.sizes[9]}px;
  padding: ${({ theme }): string => theme.sizes[4]}px
    ${({ theme, px }): string => theme.sizes[px || 4]}px;
  color: ${({ theme, value }): string =>
    value > 0
      ? theme.colors.grass80
      : value < 0
      ? theme.colors.rose80
      : 'ash90'};
  background-color: ${({ theme, value, hasBg }): string =>
    hasBg
      ? value > 0
        ? theme.colors.grass10
        : value < 0
        ? theme.colors.rose10
        : 'unset'
      : 'inherit'};
  border-radius: ${({ theme }): string => theme.radii.xxl};
  font-family: ${({ theme }): string => theme.fonts.body};
`;

StyledSignedText.defaultProps = { hasBg: true };

export const StyledImage = styled<ComposeType>(Image)`
  ${Compose};
`;
export const StyledImg = styled<ComposeType>(Image)`
  ${Compose};
`;

export const NextImage = styled(Image)``;

export const StyledInput = styled(OutlinedInput)(({ theme }) => ({
  height: theme.sizes[15],
  padding: theme.sizes[5],
  border: theme.borders[0],
  borderColor: theme.colors.ash50,
  borderRadius: theme.radii.sm,
  marginBottom: theme.sizes[4],
  fontSize: theme.fontSizes[2],
  transition: 'all 0.3s ease',

  // Style the input text
  '& .MuiOutlinedInput-input': {
    padding: 0,
    color: theme.colors.ash100,
    '&::placeholder': {
      color: theme.colors.black25,
      opacity: 1, // required for color to apply in most browsers
    },
  },

  // Remove the default notched border
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },

  // Style focus border using MUI's internal class
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: `${theme.borders[0]} !important`,
    borderColor: `${theme.colors.berry60} !important`,
  },
}));

export const StyledPrice = styled(StyledDiv)<{ value: number }>`
  display: flex;
  align-items: center;
  font-size: ${({ theme }): string => theme.fontSizes[2]};
  color: ${({ theme, value }): string =>
    value > 0
      ? theme.colors.grass80
      : value < 0
      ? theme.colors.rose80
      : theme.colors.ash100};
`;

export const StyledList = styled.div<{ isLink?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${({ theme }): string => theme.borders[0]};
  border-color: ${({ theme }): string => theme.colors.blue10};
  color: ${({ theme, isLink }): string =>
    isLink ? theme.colors.berry60 : theme.colors.ash80};
  height: ${({ theme }): number => theme.sizes[17]}px;
  padding: ${({ theme }): number => theme.sizes[5]}px
    ${({ theme }): number => theme.sizes[10]}px;
`;

export const StyledBorderRow = styled(StyledDiv)`
  border-bottom: ${({ theme }): string => theme.borders[0]};
  border-color: ${({ theme }): string => theme.colors.blue10};
`;

export const StyledAccountHeader = styled.div`
  align-self: start;
  font-family: ${({ theme }): string => theme.fonts.body};
  font-size: ${({ theme }): string => theme.fontSizes[4]};
  margin-top: ${({ theme }): number => theme.sizes[21]}px;
  margin-bottom: ${({ theme }): number => theme.sizes[12]}px;
`;
